import React from 'react';
import { useAuthContext } from '@hero/hero-auth/v2/AuthProvider';
import envVars from '../constants/envVars';
import { useUserStatusContext } from './UserStatus';

type IUserPolicy = {
    idle_session_minutes: number | null;
    refresh_token_duration_hours: number | null;
};

type IOrgProperties = {
    has_device_activity_report: boolean;
    has_care_billing?: boolean;
};

type IUserPolicyContext = {
    isLoading: boolean;
    userPolicy?: IUserPolicy;
    orgProperties?: IOrgProperties;
    orgPropertiesOrganizationId?: string;
};

type PolicySnapshot = {
    organizationId: string;
    auth: ReturnType<typeof useAuthContext>['auth'];
    userStatus: ReturnType<typeof useUserStatusContext>['userStatus'];
    isLoading: boolean;
    userPolicy?: IUserPolicy;
    orgProperties?: IOrgProperties;
};

const UserPolicyContext = React.createContext<IUserPolicyContext | undefined>(undefined);

export const useUserPolicyContext = () => {
    const ctx = React.useContext(UserPolicyContext);

    if (ctx === undefined) {
        throw new Error(`'useUserPolicyContext' must be used within a 'UserPolicyContextProvider'`);
    }

    return ctx;
};

const UserPolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [snapshot, setSnapshot] = React.useState<PolicySnapshot>();
    const { userStatus, activeOrgId } = useUserStatusContext();
    const { auth } = useAuthContext();

    React.useEffect(() => {
        if (!auth || !userStatus || !activeOrgId) {
            setSnapshot(undefined);
            return;
        }

        const controller = new AbortController();
        const scope = { organizationId: activeOrgId, auth, userStatus };
        setSnapshot({ ...scope, isLoading: true });

        const headers = { Authorization: `Bearer ${auth.access_token}` };

        const update = (patch: Partial<Pick<PolicySnapshot, 'userPolicy' | 'orgProperties' | 'isLoading'>>) => {
            if (controller.signal.aborted) return;
            setSnapshot((current) => {
                if (controller.signal.aborted || current?.organizationId !== scope.organizationId ||
                    current.auth !== scope.auth || current.userStatus !== scope.userStatus) return current;
                return { ...current, ...patch };
            });
        };
        const load = async <T,>(url: string): Promise<T | undefined> => {
            try {
                const response = await fetch(url, { headers, signal: controller.signal });
                if (!response.ok)
                    throw new Error('Network response was not ok');
                const data: unknown = await response.json();
                return data as T;
            } catch {
                return undefined;
            }
        };
        const policy = load<IUserPolicy>(
            `${envVars.API_ID_HOSTNAME}api/enterprise/policy?hero_organization_id=${activeOrgId}`
        ).then((userPolicy) => update({ userPolicy }));
        const properties = load<IOrgProperties>(
            `${envVars.API_RTM_HOSTNAME}api/enterprise/organization-properties?hero_organization_id=${activeOrgId}`
        ).then((orgProperties) => update({ orgProperties }));
        // One endpoint failing must not clear the other endpoint's successful result.
        void Promise.allSettled([policy, properties]).then(() => update({ isLoading: false }));
        return () => controller.abort();
    }, [auth, userStatus, activeOrgId]);

    // Hide stale data and show loading before the new scope's effect runs.
    const current = snapshot?.organizationId === activeOrgId && snapshot?.auth === auth &&
        snapshot?.userStatus === userStatus ? snapshot : undefined;
    const isLoading = Boolean(auth && userStatus && activeOrgId) && (!current || current.isLoading);

    return (
        <UserPolicyContext.Provider
            value={{
                userPolicy: current?.userPolicy,
                orgProperties: current?.orgProperties,
                orgPropertiesOrganizationId: current?.orgProperties ? current.organizationId : undefined,
                isLoading
            }}
        >
            {children}
        </UserPolicyContext.Provider>
    );
};

export default UserPolicyProvider;
